import React from 'react';
import { Calendar, Clock, Target, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { Panel, StatCard, EmptyState } from '../ui';
import { formatTime, formatTrendLabel } from './utils';

const MemberProductivityDashboard = ({
  kpis,
  dailyTrends = [],
  timeEntries = [],
  projects = [],
  trends = {},
}) => (
  <div className="space-y-6" data-testid="member-productivity-dashboard">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div>
        <StatCard
          icon={Clock}
          tone="primary"
          label="Total Time"
          value={`${(kpis.totalHours || 0).toFixed(1)}h`}
        />
        <div className="text-sm text-center mt-1 text-secondary-600">
          {formatTrendLabel(trends.hours?.deltaPct)} vs last period
        </div>
      </div>
      <StatCard
        icon={Target}
        tone="green"
        label="Billable Rate"
        value={`${(kpis.billablePercentage || 0).toFixed(1)}%`}
      />
      <StatCard icon={Calendar} tone="blue" label="Time Entries" value={timeEntries.length} />
      <StatCard
        icon={TrendingUp}
        tone="purple"
        label="Daily Average"
        value={`${(kpis.avgProductivity || 0).toFixed(1)}h`}
      />
    </div>

    <Panel>
      <h3 className="text-lg font-semibold mb-4">Productivity Trends</h3>
      {dailyTrends.length === 0 ? (
        <EmptyState title="No trend data" description="Log time entries to see daily trends." />
      ) : (
        <div className="space-y-3">
          {dailyTrends.slice(-7).map((day) => (
            <div
              key={day.date}
              className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg"
            >
              <div>
                <div className="font-medium">{format(new Date(day.date), 'EEEE, MMM d')}</div>
                <div className="text-sm text-secondary-600">{day.entries} entries</div>
              </div>
              <div className="text-right">
                <div className="font-medium">{day.totalHours.toFixed(1)}h</div>
                <div className="text-sm text-secondary-600">
                  {day.billableHours.toFixed(1)}h billable
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>

    <Panel>
      <h3 className="text-lg font-semibold mb-4">Time by Project</h3>
      <div className="space-y-3">
        {projects
          .map((project) => {
            const projectEntries = timeEntries.filter((entry) => entry.project_id === project.id);
            const projectTime = projectEntries.reduce(
              (sum, entry) => sum + (entry.duration || 0),
              0
            );
            const billableTime = projectEntries
              .filter((entry) => entry.is_billable)
              .reduce((sum, entry) => sum + (entry.duration || 0), 0);
            if (projectTime === 0) return null;
            return (
              <div
                key={project.id}
                className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg"
              >
                <div>
                  <h4 className="font-medium text-secondary-900">{project.name}</h4>
                  <p className="text-sm text-secondary-600">
                    {projectEntries.length} entries • {formatTime(billableTime)} billable
                  </p>
                </div>
                <div className="text-right">
                  <div className="font-medium">{formatTime(projectTime)}</div>
                  <div className="text-sm text-secondary-500">
                    {((billableTime / projectTime) * 100).toFixed(1)}% billable
                  </div>
                </div>
              </div>
            );
          })
          .filter(Boolean)}
        {projects.every(
          (p) => !timeEntries.some((e) => e.project_id === p.id && (e.duration || 0) > 0)
        ) && (
          <EmptyState title="No project time" description="Your project breakdown will appear here." />
        )}
      </div>
    </Panel>
  </div>
);

export default MemberProductivityDashboard;
